import {
  Constants,
  Deductions,
  isUkcRallyShow,
  isSameUkcRallyClass,
  toUkcRallyFormat,
  ukcRallyShowFormat,
  ukcRallySignLevels,
  ukcRallyTableLevel,
  UkcRallyClassFormat,
} from "@eagerdog/constants";
import { IShow, IShowElementExercise } from "@eagerdog/interfaces";

/**
 * UKC Rally show setup works in the canonical class form (level "RO1" + section "B").
 * Shows created before the refactor store level "RO1 B"; the form edits a canonical copy
 * and converts back to the show's own format on save so live shows keep one format.
 */

const SHOW_ELEMENT_META_KEYS = new Set(["_id", "__v", "created_at", "updated_at"]);

export const isUkcRallyShowForm = (sanctioningClub?: string, showType?: string): boolean =>
  isUkcRallyShow(sanctioningClub, showType);

/** The format the saved show uses; new shows are canonical. */
export const getUkcRallyShowFormat = (show?: IShow): UkcRallyClassFormat =>
  ukcRallyShowFormat(show?.show_elements);

/** Canonical copy of a UKC Rally show (show_elements + ring dog_classes); other shows pass through. */
export const normaliseUkcRallyShow = (show: IShow | undefined, sanctioningClub?: string): IShow | undefined => {
  if (!show || !isUkcRallyShow(sanctioningClub, show.show_type)) return show;
  if (getUkcRallyShowFormat(show) === "canonical") return show;
  return convertUkcRallyShowFormat(show, "canonical");
};

export const convertUkcRallyShowFormat = (show: IShow, format: UkcRallyClassFormat): IShow => {
  const show_elements = (show.show_elements || []).map((se: any) => toUkcRallyFormat(se, format));
  const rings = convertUkcRallyRingsFormat(show.rings, format);
  return { ...show, show_elements, ...(rings ? { rings } : {}) } as IShow;
};

export const convertUkcRallyRingsFormat = (rings: any[] | undefined, format: UkcRallyClassFormat): any[] | undefined => {
  if (!rings) return rings;
  return rings.map((ring: any) => ({
    ...ring,
    running_order: (ring.running_order || []).map((ro: any) => ({
      ...ro,
      dog_classes: (ro.dog_classes || []).map((dc: any) =>
        dc?.type === Constants.running_order_dog_class_type.dog_class || dc?.level
          ? toUkcRallyFormat(dc, format)
          : dc
      ),
    })),
  }));
};

/**
 * Full eligible sign pool for a UKC Rally class (current level + all lower levels), merged
 * with the exercises already saved on the element so enabled/disabled/order state survives.
 * Works for legacy "RO2 B" labels and canonical "RO2".
 */
export const buildUkcRallyExercises = (
  level: string,
  existingExercises?: IShowElementExercise[]
): IShowElementExercise[] | undefined => {
  const rallyDeductions = Deductions[Constants.sanctioning_club.UKC]?.[Constants.show_type.rally_obedience];
  if (!rallyDeductions || rallyDeductions[ukcRallyTableLevel(level)] === undefined) return undefined;

  const eligiblePool = new Map<string, IShowElementExercise>();
  for (const includeLevel of ukcRallySignLevels(level)) {
    const deductionDefinitions: any[] = rallyDeductions[includeLevel]?.["I"] || [];
    for (const def of deductionDefinitions) {
      const sign = def.sign;
      // Default every sign to Available; only START/FINISH default to Selected. Users build
      // their course from scratch by dragging signs over in the Manage Show exercises tab.
      const isSpecialSign = ["START", "FINISH"].includes(sign ?? "");
      const exercise: IShowElementExercise = { exercise: def.exercise, disabled: !isSpecialSign };
      if (sign !== undefined) exercise.sign = sign;
      eligiblePool.set(def.exercise, exercise);
    }
  }

  const exercises: IShowElementExercise[] = [];
  if (existingExercises && existingExercises.length > 0) {
    // Existing show: keep saved exercises in their saved order, preserving disabled state
    const savedKeys = new Set<string>();
    for (const saved of existingExercises) {
      const eligible = eligiblePool.get(saved.exercise);
      if (eligible) {
        exercises.push({ ...eligible, disabled: saved.disabled });
        savedKeys.add(saved.exercise);
      }
    }
    for (const [name, ex] of eligiblePool) {
      if (!savedKeys.has(name)) exercises.push(ex);
    }
  } else {
    for (const ex of eligiblePool.values()) exercises.push(ex);
  }
  return exercises;
};

/**
 * Post-processes nested-setup output for a UKC Rally show: carries over settings the setup
 * tabs don't edit (order_of_exercises, course images, ...), builds the sign pool, and
 * writes the result in the saved show's format.
 */
export const finaliseUkcRallyShowElements = (
  builtElements: any[],
  canonicalShow: IShow | undefined,
  format: UkcRallyClassFormat
): any[] => {
  const existing = canonicalShow?.show_elements || [];
  return builtElements.map((built) => {
    const element: any = { ...built };
    const prior: any = existing.find((se: any) =>
      se.show_element === element.show_element &&
      (se.level ? isSameUkcRallyClass(se, element) : !element.level)
    );
    if (prior) {
      Object.keys(prior).forEach((key) => {
        if (!element.hasOwnProperty(key) && !SHOW_ELEMENT_META_KEYS.has(key)) {
          element[key] = prior[key];
        }
      });
    }
    if (element.level) {
      const exercises = buildUkcRallyExercises(element.level, prior?.exercises);
      if (exercises) element.exercises = exercises;
    }
    return toUkcRallyFormat(element, format);
  });
};
